import { useContext, useState } from 'react';
import {
	Button,
	Card,
	Col,
	DatePicker,
	Empty,
	Input,
	InputNumber,
	Modal,
	Row,
	Space,
	Spin,
	Statistic,
	Table,
	Tag,
	Tooltip,
	message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import axios from '../../config/axios';
import { AuthContext } from '../../context/auth.context';
import AppLayout from '../../layouts/app.layout';
import { handleAPIError } from '../../utils/helpers';

interface AgentActivity {
	agent_id: number;
	name: string;
	phone_number: string | null;
	status: string;
	shop: string | null;
	days_worked: number;
	days_complete: number;
	days_auto_closed: number;
	days_invalid: number;
	hours: string;
	hours_per_day: string;
	claims: number;
	sales_matched: number;
	claims_rejected: number;
	claims_open: number;
	retailers_submitted: number;
	retailers_approved: number;
	retailers_pending: number;
	retailers_rejected: number;
	visits: number;
	flagged_visits: number;
}

type Metric = keyof Pick<
	AgentActivity,
	| 'claims'
	| 'sales_matched'
	| 'claims_rejected'
	| 'claims_open'
	| 'retailers_submitted'
	| 'retailers_approved'
	| 'retailers_pending'
	| 'retailers_rejected'
	| 'visits'
	| 'flagged_visits'
	| 'days_worked'
	| 'days_complete'
	| 'days_invalid'
>;

interface ActivityReport {
	from: string;
	to: string;
	agents: AgentActivity[];
	totals: Record<string, number | string>;
}

interface ClaimRow {
	id: number;
	agent: string;
	imei: string;
	state: string;
	reject_reason: string | null;
	review_reason: string | null;
	granted: boolean;
	device_id: number | null;
}

interface DetailResponse {
	metric: Metric;
	kind: 'claim' | 'retailer' | 'visit' | 'session';
	rows: Record<string, never>[];
}

const { RangePicker } = DatePicker;

const METRIC_LABELS: Record<Metric, string> = {
	claims: 'Claims',
	sales_matched: 'Matched sales',
	claims_rejected: 'Rejected claims',
	claims_open: 'Open claims',
	retailers_submitted: 'Retailers submitted',
	retailers_approved: 'Retailers approved',
	retailers_pending: 'Retailers awaiting review',
	retailers_rejected: 'Retailers rejected',
	visits: 'Visits',
	flagged_visits: 'Flagged visits',
	days_worked: 'Days out',
	days_complete: 'Days closed properly',
	days_invalid: 'Unusable days',
};

const STATE_COLOR: Record<string, string> = {
	matched: 'success',
	rejected: 'error',
	submitted: 'processing',
	contested: 'warning',
	expired: 'default',
	approved: 'success',
	pending: 'warning',
};

const time = (v: string | null) => (v ? dayjs(v).format('DD MMM HH:mm') : '—');

export default function FieldActivityReport() {
	const { user } = useContext(AuthContext);
	const isSuperAdmin = user?.is_superuser || user?.admin_type === 'super_admin';
	const queryClient = useQueryClient();

	const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs()]);
	const [drill, setDrill] = useState<{ metric: Metric; agentId?: number; who: string } | null>(
		null,
	);
	const [overriding, setOverriding] = useState<ClaimRow | null>(null);
	const [overrideReason, setOverrideReason] = useState('');
	const [overrideDevice, setOverrideDevice] = useState<number | null>(null);

	const from = range[0].format('YYYY-MM-DD');
	const to = range[1].format('YYYY-MM-DD');

	const { data, isLoading, error } = useQuery<ActivityReport>({
		queryKey: ['field-activity', from, to],
		queryFn: async () => {
			const res = await axios.get('/admin/field/reports/activity', {
				params: { from, to },
				withCredentials: true,
			});
			return res.data.results;
		},
	});
	if (error) handleAPIError(error);

	const detail = useQuery<DetailResponse>({
		queryKey: ['field-activity-detail', from, to, drill?.metric, drill?.agentId],
		enabled: !!drill,
		queryFn: async () => {
			const res = await axios.get('/admin/field/reports/activity/detail', {
				params: { from, to, metric: drill!.metric, agent_id: drill!.agentId },
				withCredentials: true,
			});
			return res.data.results;
		},
	});

	const closeOverride = () => {
		setOverriding(null);
		setOverrideReason('');
		setOverrideDevice(null);
	};

	const override = useMutation({
		mutationFn: async (claim: ClaimRow) => {
			await axios.post(
				`/admin/field/claims/${claim.id}/override`,
				{ reason: overrideReason.trim(), device_id: overrideDevice ?? undefined },
				{ withCredentials: true },
			);
		},
		onSuccess: () => {
			message.success('Claim approved');
			queryClient.invalidateQueries({ queryKey: ['field-activity'] });
			queryClient.invalidateQueries({ queryKey: ['field-activity-detail'] });
			closeOverride();
		},
		onError: handleAPIError,
	});

	// A count is only worth opening when there is something behind it.
	const num = (value: number, metric: Metric, row?: AgentActivity, color?: string) => {
		if (!value) return <span style={{ color: '#ccc' }}>—</span>;
		const inner = color ? <Tag color={color}>{value}</Tag> : value;
		return (
			<Button
				type="link"
				size="small"
				style={{ padding: 0, height: 'auto' }}
				onClick={() =>
					setDrill({
						metric,
						agentId: row?.agent_id,
						who: row ? row.name : 'All agents',
					})
				}
			>
				{inner}
			</Button>
		);
	};

	const columns = [
		{
			title: 'Agent',
			fixed: 'left' as const,
			width: 185,
			render: (row: AgentActivity) => (
				<div>
					<div style={{ fontWeight: 500 }}>{row.name}</div>
					<div style={{ fontSize: 12, color: '#888' }}>
						{row.phone_number}
						{row.status !== 'active' && (
							<Tag style={{ marginLeft: 6 }}>{row.status}</Tag>
						)}
					</div>
				</div>
			),
		},
		{
			title: 'Time on the ground',
			children: [
				{
					title: 'Days out',
					width: 90,
					sorter: (a: AgentActivity, b: AgentActivity) => a.days_worked - b.days_worked,
					render: (r: AgentActivity) => num(r.days_worked, 'days_worked', r),
				},
				{
					title: <Tooltip title="Closed by the agent, clocks in order">Closed</Tooltip>,
					width: 90,
					render: (r: AgentActivity) => num(r.days_complete, 'days_complete', r),
				},
				{
					title: 'Hours',
					dataIndex: 'hours',
					width: 85,
					sorter: (a: AgentActivity, b: AgentActivity) => Number(a.hours) - Number(b.hours),
				},
				{ title: 'Per day', dataIndex: 'hours_per_day', width: 85 },
				{
					title: (
						<Tooltip title="Never checked out, or check-out before check-in">Unusable</Tooltip>
					),
					width: 95,
					render: (r: AgentActivity) =>
						num(r.days_auto_closed + r.days_invalid, 'days_invalid', r, 'warning'),
				},
			],
		},
		{
			title: 'Sales',
			children: [
				{
					title: 'Matched',
					width: 90,
					sorter: (a: AgentActivity, b: AgentActivity) => a.sales_matched - b.sales_matched,
					render: (r: AgentActivity) => num(r.sales_matched, 'sales_matched', r, 'success'),
				},
				{
					title: 'Claimed',
					width: 90,
					render: (r: AgentActivity) => num(r.claims, 'claims', r),
				},
				{
					title: 'Open',
					width: 80,
					render: (r: AgentActivity) => num(r.claims_open, 'claims_open', r, 'processing'),
				},
				{
					title: 'Rejected',
					width: 90,
					render: (r: AgentActivity) => num(r.claims_rejected, 'claims_rejected', r, 'error'),
				},
			],
		},
		{
			title: 'Retailers',
			children: [
				{
					title: 'Submitted',
					width: 100,
					sorter: (a: AgentActivity, b: AgentActivity) =>
						a.retailers_submitted - b.retailers_submitted,
					render: (r: AgentActivity) => num(r.retailers_submitted, 'retailers_submitted', r),
				},
				{
					title: 'Approved',
					width: 100,
					render: (r: AgentActivity) =>
						num(r.retailers_approved, 'retailers_approved', r, 'success'),
				},
				{
					title: 'Awaiting',
					width: 95,
					render: (r: AgentActivity) =>
						num(r.retailers_pending, 'retailers_pending', r, 'warning'),
				},
			],
		},
		{
			title: 'Visits',
			children: [
				{
					title: 'Total',
					width: 80,
					render: (r: AgentActivity) => num(r.visits, 'visits', r),
				},
				{
					title: (
						<Tooltip title="Mock location, poor accuracy, or photo taken elsewhere">
							Flagged
						</Tooltip>
					),
					width: 90,
					render: (r: AgentActivity) => num(r.flagged_visits, 'flagged_visits', r, 'error'),
				},
			],
		},
	];

	const detailColumns = (() => {
		switch (detail.data?.kind) {
			case 'claim':
				return [
					{ title: 'IMEI', dataIndex: 'imei' },
					{
						title: 'State',
						dataIndex: 'state',
						render: (s: string) => <Tag color={STATE_COLOR[s]}>{s}</Tag>,
					},
					{ title: 'Retailer', dataIndex: 'retailer' },
					{ title: 'Entry', dataIndex: 'entry_method' },
					{
						title: 'Reason',
						render: (r: ClaimRow) =>
							r.granted ? <Tag color="purple">granted</Tag> : r.reject_reason ?? r.review_reason,
					},
					{ title: 'Claimed', dataIndex: 'claimed_at', render: time },
					...(isSuperAdmin
						? [
								{
									title: '',
									width: 90,
									render: (r: ClaimRow) =>
										r.state === 'rejected' && (
											<Button size="small" onClick={() => setOverriding(r)}>
												Approve
											</Button>
										),
								},
							]
						: []),
				];
			case 'retailer':
				return [
					{ title: 'Business', dataIndex: 'business_name' },
					{ title: 'Owner', dataIndex: 'owner_name' },
					{ title: 'Phone', dataIndex: 'msisdn' },
					{ title: 'Town', dataIndex: 'town' },
					{
						title: 'Status',
						dataIndex: 'status',
						render: (s: string) => <Tag color={STATE_COLOR[s]}>{s}</Tag>,
					},
					{ title: 'Vendor ID', dataIndex: 'vendor_id' },
					{ title: 'Submitted', dataIndex: 'submitted_at', render: time },
					{ title: 'Reviewed', dataIndex: 'reviewed_at', render: time },
				];
			case 'visit':
				return [
					{ title: 'Retailer', dataIndex: 'retailer' },
					{ title: 'Outcome', dataIndex: 'outcome_code' },
					{
						title: 'Flags',
						dataIndex: 'flags',
						render: (f: string[]) =>
							f?.length ? f.map((x) => <Tag color="error" key={x}>{x}</Tag>) : '—',
					},
					{
						title: 'Distance',
						dataIndex: 'distance_m',
						render: (d: number | null) => (d == null ? '—' : `${Math.round(d)} m`),
					},
					{ title: 'Notes', dataIndex: 'notes' },
					{ title: 'Visited', dataIndex: 'visited_at', render: time },
				];
			case 'session':
				return [
					{ title: 'Date', dataIndex: 'work_date' },
					{ title: 'In', dataIndex: 'checked_in', render: time },
					{ title: 'Out', dataIndex: 'checked_out', render: time },
					{ title: 'Hours', dataIndex: 'hours', render: (h: string | null) => h ?? '—' },
					{
						title: 'Issue',
						render: (r: { auto_closed: boolean; reversed_clock: boolean; checked_out: string | null }) =>
							r.reversed_clock ? (
								<Tag color="error">check-out before check-in</Tag>
							) : r.auto_closed ? (
								<Tag color="warning">closed by the server</Tag>
							) : !r.checked_out ? (
								<Tag color="warning">never checked out</Tag>
							) : (
								'—'
							),
					},
				];
			default:
				return [];
		}
	})();

	const totals = data?.totals;

	return (
		<AppLayout title="Agent Activity" subtitle="What the field team did">
			<div style={{ padding: 16 }}>
				<Card size="small" style={{ marginBottom: 16 }}>
					<Row gutter={16} align="middle">
						<Col>
							<RangePicker
								value={range}
								allowClear={false}
								onChange={(v) => v && setRange(v as [Dayjs, Dayjs])}
							/>
						</Col>
						{totals && (
							<>
								<Col flex="auto" />
								<Col>
									<Statistic title="Agents" value={totals.agents as number} />
								</Col>
								<Col>
									<Statistic title="Days out" value={totals.days_worked as number} />
								</Col>
								<Col>
									<Statistic title="Hours" value={totals.hours as string} />
								</Col>
								<Col>
									<Statistic title="Sales" value={totals.sales_matched as number} />
								</Col>
								<Col>
									<Statistic
										title="Retailers approved"
										value={totals.retailers_approved as number}
									/>
								</Col>
							</>
						)}
					</Row>
				</Card>

				{isLoading ? (
					<div style={{ padding: 48, textAlign: 'center' }}>
						<Spin />
					</div>
				) : (
					<Table
						size="small"
						rowKey="agent_id"
						columns={columns}
						dataSource={data?.agents ?? []}
						pagination={false}
						scroll={{ x: 1250 }}
						bordered
						locale={{ emptyText: <Empty description="No agents" /> }}
					/>
				)}

				<Modal
					open={!!drill}
					onCancel={() => setDrill(null)}
					footer={null}
					width={1000}
					title={
						drill && (
							<span>
								{METRIC_LABELS[drill.metric]} · {drill.who}
								<span style={{ fontWeight: 400, color: '#888', marginLeft: 8 }}>
									{dayjs(from).format('DD MMM')} – {dayjs(to).format('DD MMM YYYY')}
								</span>
							</span>
						)
					}
				>
					{detail.isFetching ? (
						<div style={{ padding: 32, textAlign: 'center' }}>
							<Spin />
						</div>
					) : (
						<Table
							size="small"
							rowKey="id"
							columns={detailColumns}
							dataSource={detail.data?.rows ?? []}
							pagination={{ pageSize: 10, hideOnSinglePage: true }}
							scroll={{ x: 800 }}
						/>
					)}
				</Modal>

				<Modal
					open={!!overriding}
					title={overriding && `Approve ${overriding.imei} for ${overriding.agent}?`}
					okText="Approve claim"
					okButtonProps={{ disabled: !overrideReason.trim() }}
					confirmLoading={override.isPending}
					onCancel={closeOverride}
					onOk={() => overriding && override.mutate(overriding)}
				>
					<Space direction="vertical" size={8} style={{ width: '100%' }}>
						{overriding && !overriding.device_id && (
							<InputNumber
								style={{ width: '100%' }}
								placeholder="Contract ID"
								min={1}
								precision={0}
								value={overrideDevice}
								onChange={(v) => setOverrideDevice(v)}
							/>
						)}
						<Input.TextArea
							rows={3}
							placeholder="Reason"
							maxLength={255}
							value={overrideReason}
							onChange={(e) => setOverrideReason(e.target.value)}
						/>
					</Space>
				</Modal>
			</div>
		</AppLayout>
	);
}
